'use client';

import Link from 'next/link';
import {
  MapPin,
  Star,
  Wifi,
  UtensilsCrossed,
  Car,
  Waves,
  GitCompareArrows,
  Check,
  Navigation,
  Heart,
  Shield,
  Clock,
} from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { useTranslation } from '@/core/i18n';
import type { Hotel } from '../types/hotels.types';

export type { Hotel };

/* -------------------------------------------------------------------------- */
/*  Deterministic formatters (SSR-safe)                                       */
/* -------------------------------------------------------------------------- */

export function formatIndianNumber(n: number): string {
  const s = n.toString();
  if (s.length <= 3) return s;
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3);
  return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
}

/**
 * A rupee amount, or an honest gap.
 *
 * formatIndianNumber(null) yields "0" and undefined yields "NaN", so a
 * property with no rates rendered as "Rs 0" — read as free, not as "not yet
 * priced". 56 of 137 live properties are in that state: every hotel that has
 * not created a room type (D-26/D-27).
 *
 * Shared by HotelCard, HotelCompare, HotelMapView and ChainGSTReport, so the
 * absence is handled once here rather than at each call site.
 */
export function formatINR(n: number | null | undefined): string {
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) {
    return 'Price on request';
  }
  return '\u20B9' + formatIndianNumber(n);
}

function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

/* -------------------------------------------------------------------------- */
/*  Amenity icon helper                                                       */
/* -------------------------------------------------------------------------- */

function AmenityIcon({ name }: { name: string }) {
  const cls = 'h-3 w-3';
  switch (name) {
    case 'Wi-Fi':
      return <Wifi className={cls} />;
    case 'Restaurant':
      return <UtensilsCrossed className={cls} />;
    case 'Pool':
      return <Waves className={cls} />;
    case 'Parking':
      return <Car className={cls} />;
    default:
      return null;
  }
}

/* -------------------------------------------------------------------------- */
/*  Star rating display                                                       */
/* -------------------------------------------------------------------------- */

function StarRating({ count }: { count: number }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <Star key={i} className="h-3 w-3 fill-hms-gold text-hms-gold" />
      ))}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  HotelCard                                                                 */
/* -------------------------------------------------------------------------- */

interface HotelCardProps {
  hotel: Hotel;
  dateQuery: string;
  distanceKm?: number | null;
  isCompareSelected: boolean;
  onToggleCompare: (hotelId: string) => void;
  compareDisabled: boolean;
  isSaved?: boolean;
  onToggleSave?: (hotelId: string) => void;
}

export function HotelCard({
  hotel,
  dateQuery,
  distanceKm,
  isCompareSelected,
  onToggleCompare,
  compareDisabled,
  isSaved = false,
  onToggleSave,
}: HotelCardProps) {
  const { t } = useTranslation();
  const detailUrl = `/hotels/${hotel.id}${dateQuery ? `?${dateQuery}` : ''}`;
  return (
    <Link id={`hotels-search-card-${hotel.id}`} href={detailUrl} className="group block cursor-pointer">
      <div
        className={cn(
          'overflow-hidden rounded-xl border bg-hms-card',
          'transition-all duration-300',
          isCompareSelected
            ? 'border-hms-gold/50 shadow-md shadow-hms-gold/5'
            : 'border-hms-border',
          'hover:scale-[1.02] hover:border-hms-gold/30 hover:shadow-lg hover:shadow-hms-gold/5',
        )}
      >
        {/* Hotel image */}
        <div className="relative h-44 overflow-hidden">
          <img
            src={hotel.image}
            alt={hotel.name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
          {/* Compare checkbox */}
          <button
            id={`hotels-search-compare-${hotel.id}`}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleCompare(hotel.id);
            }}
            disabled={compareDisabled && !isCompareSelected}
            className={cn(
              'absolute left-2 top-2 flex h-9 w-9 items-center justify-center rounded-lg',
              'transition-all backdrop-blur-sm',
              isCompareSelected
                ? 'bg-hms-gold text-black'
                : 'bg-black/50 text-hms-textSub hover:bg-black/70 hover:text-hms-text',
              compareDisabled && !isCompareSelected && 'cursor-not-allowed opacity-40',
            )}
            aria-label={`${isCompareSelected ? 'Remove from' : 'Add to'} comparison`}
          >
            {isCompareSelected ? (
              <Check className="h-3.5 w-3.5" />
            ) : (
              <GitCompareArrows className="h-3.5 w-3.5" />
            )}
          </button>
          {/* Wishlist button */}
          {onToggleSave && (
            <button
              id={`hotels-search-save-${hotel.id}`}
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleSave(hotel.id); }}
              className={cn(
                'absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full',
                'transition-all backdrop-blur-sm',
                isSaved ? 'bg-hms-red/90 text-white' : 'bg-black/50 text-white/70 hover:bg-black/70 hover:text-white',
              )}
              aria-label={isSaved ? 'Remove from wishlist' : 'Save to wishlist'}
            >
              <Heart className={cn('h-3.5 w-3.5', isSaved && 'fill-current')} />
            </button>
          )}
          {/* Type badge overlay */}
          <span
            className={cn(
              'absolute left-3 bottom-3 rounded-md px-2 py-0.5',
              'bg-black/50 text-[10px] font-semibold uppercase tracking-wider text-hms-textSub backdrop-blur-sm',
            )}
          >
            {hotel.type}
          </span>
          {/* Distance badge */}
          {distanceKm !== null && distanceKm !== undefined && (
            <span
              id={`hotels-search-distance-${hotel.id}`}
              className={cn(
                'absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-md px-2 py-0.5',
                'bg-hms-teal/90 text-[10px] font-semibold text-white backdrop-blur-sm',
              )}
            >
              <Navigation className="h-2.5 w-2.5" />
              {formatDistance(distanceKm)}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="space-y-3 p-4">
          {/* Name & star rating */}
          <div>
            <h3 className="text-sm font-semibold text-hms-text transition-colors group-hover:text-hms-gold truncate">
              {hotel.name}
            </h3>
            <div className="mt-1 flex items-center gap-2">
              <StarRating count={hotel.stars} />
              <span className="text-[10px] text-hms-textDim">{hotel.stars}{t('hotelCard.star')}</span>
            </div>
          </div>

          {/* Location + last booked */}
          <div className="flex items-center gap-1.5 text-xs text-hms-textSub">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-hms-teal" />
            <span className="truncate">{hotel.location}</span>
            {distanceKm !== null && distanceKm !== undefined && (
              <span className="ml-auto shrink-0 text-[10px] font-medium text-hms-teal">
                {formatDistance(distanceKm)} {t('hotelCard.away')}
              </span>
            )}
          </div>
          {hotel.lastBooked && (
            <p id={`hotels-search-lastbooked-${hotel.id}`} className="flex items-center gap-1 text-[10px] text-hms-textDim">
              <Clock className="h-3 w-3" /> {t('hotelCard.lastBooked')} {hotel.lastBooked}
            </p>
          )}

          {/* Amenities */}
          <div className="flex flex-wrap gap-1.5">
            {hotel.amenities.map((a) => (
              <span
                key={a}
                className={cn(
                  'inline-flex items-center gap-1 rounded-md px-2 py-0.5',
                  'bg-white/5 text-[10px] font-medium text-hms-textSub',
                )}
              >
                <AmenityIcon name={a} />
                {a}
              </span>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-hms-border/50" />

          {/* Rating & Price */}
          <div className="flex items-end justify-between">
            <div className="flex items-center gap-1.5">
              <span
                className={cn(
                  'inline-flex h-7 items-center gap-1 rounded-md px-2',
                  'bg-hms-gold/10 text-xs font-bold text-hms-gold',
                )}
              >
                <Star className="h-3 w-3 fill-hms-gold text-hms-gold" />
                {hotel.rating}
              </span>
              <span className="text-[10px] text-hms-textDim">
                ({formatIndianNumber(hotel.reviews)} {t('hotelCard.reviews')})
              </span>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-hms-text">
                <span className="mr-0.5 text-xs font-normal text-hms-textDim">{t('hotelCard.from')}</span>
                {formatINR(hotel.price)}
              </p>
              <p className="text-[10px] text-hms-textDim">{t('hotelCard.perNight')}</p>
            </div>
          </div>

          {/* Badges row */}
          <div className="flex flex-wrap items-center gap-1.5">
            {hotel.availability !== undefined && hotel.availability <= 3 && (
              <span id={`hotels-search-avail-${hotel.id}`} className="inline-flex items-center gap-1 rounded-md bg-hms-amber/10 px-2 py-0.5 text-[9px] font-semibold text-hms-amber">
                {hotel.availability !== 1 ? t('hotelCard.onlyRoomsLeftPlural').replace('{count}', String(hotel.availability)) : t('hotelCard.onlyRoomsLeft').replace('{count}', String(hotel.availability))}
              </span>
            )}
            {hotel.freeCancellation && (
              <span className="inline-flex items-center gap-1 rounded-md bg-hms-teal/10 px-2 py-0.5 text-[9px] font-semibold text-hms-teal">
                <Shield className="h-2.5 w-2.5" /> {t('hotelCard.freeCancellation')}
              </span>
            )}
          </div>

          {/* View Details indicator */}
          <span
            id={`hotels-search-view-${hotel.id}`}
            className={cn(
              'mt-1 flex h-9 w-full items-center justify-center rounded-lg',
              'border border-hms-gold/30 text-xs font-semibold text-hms-gold',
              'transition-all group-hover:bg-hms-gold/10',
            )}
          >
            {t('hotelCard.viewDetails')}
          </span>
        </div>
      </div>
    </Link>
  );
}
